import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import broImage from "@assets/images/bro.svg";
import { config } from "@core/config/config";
import { Group } from "@domain/group/group";
import { useIsLearner } from "@domain/currentProfile/hooks";
import { useGroupLivecourse, useOrganizationLivecourse } from "@domain/livecourse/hooks";
import { Carousel } from "@ui/components/carousel/carousel";
import { stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import {
	CreateGrouplivecoursesThumbnail,
	CreateOrganizationlivecoursesThumbnail,
} from "../livecourses/createLivecoursesThumbnail";
import { GroupLivecoursesThumbnail } from "@ui/pages/livecourses/livecoursesThumbnail/groupLivecoursesThumbnail";
import { OrganizationLivecoursesThumbnail } from "@ui/pages/livecourses/livecoursesThumbnail/organizationLivecoursesThumbnail";

interface LivecoursesCarouselGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
}

interface LivecoursesCarouselOrganizationProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
	isAdmin: boolean;
}

export const LivecoursesCarouselGroup: React.FC<LivecoursesCarouselGroupProps> = ({ shareroom, ...htmlProps }) => {
	const { result: livecourses, loading } = useGroupLivecourse(shareroom.id);
	const isLearner = useIsLearner();
	const isAdmin = shareroom.userRole === "ADMINISTRATOR" && !isLearner;

	return (
		<Container {...htmlProps}>
			{!config.features.classrooms.enableLive ? (
				<ComingSoonMessage>{I18n.t("live.coming_soon")}</ComingSoonMessage>
			) : null}

			<div
				style={
					config.features.classrooms.enableLive
						? undefined
						: { pointerEvents: "none", userSelect: "none", opacity: 0.5 }
				}
			>
				{!isAdmin && !livecourses?.length ? (
					<NoLiveBloc>
						<img src={broImage} />
						<EmptyMessage>{I18n.t("live.no_live")}</EmptyMessage>
					</NoLiveBloc>
				) : (
					<Carousel
						title={I18n.t("live.title")}
						itemHeight={270}
						itemWidth={330}
						loading={loading}
						items={isAdmin ? ["firstItem", ...livecourses] : livecourses}
						renderItem={(livecourse) =>
							livecourse === "firstItem" ? (
								<CreateGrouplivecoursesThumbnail key={shareroom.id} shareroom={shareroom} />
							) : livecourse === undefined || typeof livecourse === "string" ? undefined : (
								<GroupLivecoursesThumbnail key={livecourse.id} shareroom={shareroom} livecourse={livecourse} />
							)
						}
					/>
				)}
			</div>
		</Container>
	);
};

export const LivecoursesCarouselOrganization: React.FC<LivecoursesCarouselOrganizationProps> = ({
	organizationId,
	isAdmin = false,
	...htmlProps
}) => {
	const { result: livecourses, loading } = useOrganizationLivecourse(organizationId);

	return loading ? (
		<></>
	) : (
		<Container {...htmlProps}>
			{!isAdmin && !livecourses?.length ? (
				<NoLiveBloc>
					<img src={broImage} />
					<EmptyMessage>{I18n.t("live.no_live")}</EmptyMessage>
				</NoLiveBloc>
			) : (
				<Carousel
					title={I18n.t("live.title")}
					itemHeight={347}
					itemWidth={330}
					loading={loading}
					items={isAdmin ? ["firstItem", ...livecourses] : livecourses}
					renderItem={(livecourse) =>
						livecourse === "firstItem" ? (
							<CreateOrganizationlivecoursesThumbnail organizationId={organizationId} />
						) : livecourse === undefined || typeof livecourse === "string" ? undefined : (
							<OrganizationLivecoursesThumbnail
								key={livecourse.id}
								organizationId={organizationId}
								livecourse={livecourse}
								isAdmin={isAdmin}
							/>
						)
					}
				/>
			)}
		</Container>
	);
};
const Container = styled.div``;

const ComingSoonMessage = styled.div`
	font-family: Sora;
	font-size: 16px;
	line-height: 26px;
	color: rgba(1, 1, 1);
	margin-top: 20px;
	margin-bottom: 20px;
	text-align: center;
	@media ${mobile} {
		font-size: 13px;
	}
`;

const NoLiveBloc = styled.div`
	${stack(20, "center", "center")};
	width: 100%;
	> img {
		width: 400px;
	}
`;
const EmptyMessage = styled.div`
	font-weight: 400;
	font-size: 16px;
	line-height: 26px;
	color: rgba(1, 1, 1, 0.5);
	margin-top: 20px;
	@media ${mobile} {
		font-size: 13px;
	}
`;
